/**
 * MicroTransactionsPanel — right context panel in InternalEditEntry.
 *
 * A single-column list of the micro-transactions captured for the current entry
 * and its full lineage — for a group/merge/collapse output, this covers every
 * source entry the result was derived from, spanning multiple dates — rendered
 * as a shadcn Accordion: expanding a row reveals its raw payload inline, one
 * open at a time. The detail mounts only while its row is open, so its
 * raw_payload fetch stays lazy by construction.
 */

import { formatInTimeZone } from "date-fns-tz";
import { AppWindow, Inbox } from "lucide-react";
import { formatTime } from "@/shared/utils/formatTime";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/components/ui/accordion";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { AppIcon } from "@/v2/lib/appIcons";
import { useMicroTransactions } from "../hooks/useMicroTransactions";
import { getProcessDisplay } from "../utils/processDisplay";
import { MicroTransactionDetail } from "./MicroTransactionDetail";

interface MicroTransactionsPanelProps {
  transactionId: string;
}

const SKELETON_ROWS = ["a", "b", "c", "d", "e", "f"];

export function MicroTransactionsPanel({ transactionId }: MicroTransactionsPanelProps) {
  const { micros, isLoading, error } = useMicroTransactions(transactionId);
  const ordered = [...micros].reverse();

  return (
    <section className="flex-1 min-h-0 flex flex-col overflow-hidden">
      {/* pr-12 leaves room for the modal's close button in the top-right corner. */}
      <header className="flex h-12 items-center gap-2 pl-4 pr-12 border-b shrink-0">
        <h3 className="text-sm font-medium">Micro-transactions</h3>
        {!isLoading && !error && micros.length > 0 && (
          <span className="text-xs text-muted-foreground tabular-nums">
            · {micros.length} event{micros.length === 1 ? "" : "s"}
          </span>
        )}
      </header>

      {isLoading && (
        <div className="flex-1 overflow-hidden p-4 space-y-4">
          {SKELETON_ROWS.map((k) => (
            <div key={k} className="flex items-center gap-3">
              <Skeleton className="size-7 rounded-md" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-3.5 w-2/3" />
                <Skeleton className="h-3 w-1/4" />
              </div>
            </div>
          ))}
        </div>
      )}

      {error && !isLoading && (
        <div className="flex-1 grid place-items-center px-6 text-center text-sm text-destructive">
          Failed to load micro-transactions. {error}
        </div>
      )}

      {!isLoading && !error && micros.length === 0 && (
        <div className="flex-1 flex flex-col items-center justify-center gap-1.5 text-muted-foreground">
          <Inbox className="size-6" />
          <p className="text-sm font-medium text-foreground">No micro-transactions</p>
          <p className="text-xs">This entry has no captured activity.</p>
        </div>
      )}

      {!isLoading && !error && ordered.length > 0 && (
        <Accordion type="single" collapsible className="flex-1 overflow-y-auto">
          {ordered.map((m) => {
            const { appName } = getProcessDisplay(m.process_name);
            const time = formatTime(m.received_at);
            return (
              <AccordionItem key={m.id} value={m.id} className="last:border-b-0">
                <AccordionTrigger className="gap-3 px-4 py-3 hover:no-underline hover:bg-muted/50 data-[state=open]:bg-muted/50 [&>svg]:text-muted-foreground">
                  <span className="flex flex-1 min-w-0 items-center gap-3">
                    {appName ? (
                      <AppIcon application={appName} className="size-4 shrink-0" />
                    ) : (
                      <AppWindow className="size-4 shrink-0 text-muted-foreground" />
                    )}
                    <span className="flex-1 min-w-0 truncate text-left text-sm font-medium">
                      {m.title ?? <span className="italic text-muted-foreground">(untitled)</span>}
                    </span>
                    <span className="shrink-0 flex items-center gap-1.5 text-xs text-muted-foreground tabular-nums">
                      <span>{formatInTimeZone(m.date, "UTC", "MMM d")}</span>
                      <span aria-hidden className="opacity-50">
                        ·
                      </span>
                      <span>{time ?? "—"}</span>
                      {m.payload_size_kb != null && (
                        <>
                          <span aria-hidden className="opacity-50">
                            ·
                          </span>
                          <span>{m.payload_size_kb} KB</span>
                        </>
                      )}
                    </span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="p-0">
                  <MicroTransactionDetail micro={m} />
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}
    </section>
  );
}
